import React, { useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector } from 'react-redux';
import Button from '../../../components/button';
import ConfirmationModal from '../../../components/modal/confirmationModal';
import Spinners from '../../../components/spinner';
import Tag from '../../../components/lozenge/tags';
import Heading from '../../../components/typography';
import { DATE_FORMAT } from '../../../constants/common';
import { GoBackBlock, PageHeader, WrapperContainer } from '../../../constants/style/common.styled';
import { COLORS } from '../../../constants/style/color';
import {
  useBoolean,
  useGetAdvisorApiCredentialList,
  useGetAdvisorPartnerEnrollmentConfig,
  useGetCompanyProfileDetails,
  useGetVendorCompanyView,
  usePostAdvisorApiCredential,
  usePutAdvisorApiCredential,
  useUpsertAdvisorPartnerEnrollmentConfig,
  useWealthlaneCustomerApi,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import { selectIsAuthenticated } from '../../auth/authSlice';
import AuthService from '../../../services/authService';
import {
  type Wealthlane_Dtos_AdvisorApi_AdvisorApiCredentialDto,
  type Wealthlane_Dtos_AdvisorApi_UpsertAdvisorPartnerEnrollmentConfigDto,
} from '@wealthlane/api-services/wealthlane-identity-service';
import { useQfaiParam } from '../../../utils/qfai';
import { ADVISOR_API_CREDENTIAL_STATUS } from './schema';
import CreateAdvisorApiKeyModal, {
  type AdvisorApiKeyFormValues,
  type CreatedAdvisorApiKey,
} from './modals/CreateAdvisorApiKeyModal';
import EditAdvisorApiKeyModal from './modals/EditAdvisorApiKeyModal';
import AdvisorEnrollmentConfigModal, {
  type AdvisorEnrollmentConfigFormValues,
} from './modals/AdvisorEnrollmentConfigModal';
import {
  formatAllowedHostsForForm,
  normalizeAllowedHostsFromForm,
} from './enrollmentConfigUtils';

const formatDate = (value?: string | null): string =>
  value ? moment(value).format(DATE_FORMAT) : '-';

const formatEndDate = (value?: string | null): string =>
  value ? moment(value).format(DATE_FORMAT) : 'No expiration';

const isActiveCredential = (
  credential?: Wealthlane_Dtos_AdvisorApi_AdvisorApiCredentialDto,
): boolean => Number(credential?.status) === ADVISOR_API_CREDENTIAL_STATUS.Active;

const AdvisorApiConfigPage = (): JSX.Element => {
  const { handleRequest } = useWealthlaneIdentityApi();
  const { handleRequest: handleCustomerRequest } = useWealthlaneCustomerApi();
  const qfai = useQfaiParam<{ companyId: string }>();
  const qfaiCompanyId = qfai?.companyId;
  const isVendorContext = Boolean(qfaiCompanyId);
  const apiCompanyId = isVendorContext ? qfaiCompanyId : undefined;

  const authService = new AuthService();
  const isAuthenticated = useSelector(selectIsAuthenticated) || authService.isAuthenticated();
  const { data: companyProfile, isLoading: isCompanyProfileLoading } = useGetCompanyProfileDetails(
    handleCustomerRequest,
    isAuthenticated,
  );

  const companyId = qfaiCompanyId ?? companyProfile?.companyId;

  const { data: companyView, isLoading: isVendorCompanyLoading } = useGetVendorCompanyView(
    handleCustomerRequest,
    isVendorContext ? companyId : undefined,
  );
  const vendorCompanyDetails = companyView?.companyStepOneForm;
  const companyDetails = isVendorContext ? vendorCompanyDetails : companyProfile;
  const isCompanyLoading = isVendorContext ? isVendorCompanyLoading : isCompanyProfileLoading;
  const companyName =
    companyDetails?.name?.trim() || companyDetails?.companyName?.trim() || '';

  const { value: isEnrollmentOpen, setFalse: closeEnrollment, setTrue: openEnrollment } = useBoolean(false);
  const { value: isCreateOpen, setFalse: closeCreate, setTrue: openCreate } = useBoolean(false);
  const { value: isEditOpen, setFalse: closeEdit, setTrue: openEdit } = useBoolean(false);
  const { value: isRevokeOpen, setFalse: closeRevoke, setTrue: openRevoke } = useBoolean(false);

  const { data: credentialList, isLoading } = useGetAdvisorApiCredentialList(handleRequest, apiCompanyId);

  const {
    data: enrollmentConfig,
    isLoading: isEnrollmentConfigLoading,
  } = useGetAdvisorPartnerEnrollmentConfig(handleRequest, apiCompanyId, isEnrollmentOpen);

  const { mutateAsync: createCredential, isLoading: isCreating } = usePostAdvisorApiCredential(handleRequest);
  const { mutate: updateCredential, isLoading: isUpdating } = usePutAdvisorApiCredential(handleRequest);
  const { mutate: revokeCredential, isLoading: isRevoking } = usePutAdvisorApiCredential(
    handleRequest,
    'Advisor API key revoked successfully.',
  );
  const { mutate: upsertEnrollmentConfig, isLoading: isSavingEnrollmentConfig } =
    useUpsertAdvisorPartnerEnrollmentConfig(handleRequest);

  const [selectedCredential, setSelectedCredential] =
    useState<Wealthlane_Dtos_AdvisorApi_AdvisorApiCredentialDto>();
  const [createdApiKey, setCreatedApiKey] = useState<CreatedAdvisorApiKey | null>(null);

  const items = useMemo(
    () =>
      [...(credentialList ?? [])].sort((a, b) =>
        moment(b.effectiveFrom ?? 0).diff(moment(a.effectiveFrom ?? 0)),
      ),
    [credentialList],
  );

  const activeKeyCount = useMemo(() => items.filter(isActiveCredential).length, [items]);

  const toIsoDate = (value: string): string => moment(value).startOf('day').toISOString();

  const extractCreatedKey = (
    data?: { apiKey?: string | null; apiKeyPrefix?: string | null; ApiKey?: string | null; ApiKeyPrefix?: string | null } | null,
  ): CreatedAdvisorApiKey | null => {
    const apiKey = data?.apiKey ?? data?.ApiKey;
    if (!apiKey) return null;
    return {
      apiKey,
      apiKeyPrefix: data?.apiKeyPrefix ?? data?.ApiKeyPrefix,
    };
  };

  const enrollmentFormInitialValues: AdvisorEnrollmentConfigFormValues = {
    displayName:
      enrollmentConfig?.displayName?.trim() ||
      companyName ||
      '',
    allowedReturnUrlHosts: formatAllowedHostsForForm(enrollmentConfig?.allowedReturnUrlHosts),
    isActive: enrollmentConfig?.isActive ?? true,
  };

  const handleEnrollmentSubmit = (values: AdvisorEnrollmentConfigFormValues): void => {
    upsertEnrollmentConfig({
      ...(apiCompanyId ? { companyId: apiCompanyId } : {}),
      displayName: values.displayName.trim(),
      allowedReturnUrlHosts: normalizeAllowedHostsFromForm(values.allowedReturnUrlHosts),
      isActive: values.isActive,
    } as Wealthlane_Dtos_AdvisorApi_UpsertAdvisorPartnerEnrollmentConfigDto);
  };

  const handleCreateSubmit = async (
    values: AdvisorApiKeyFormValues,
  ): Promise<CreatedAdvisorApiKey | null | undefined> => {
    const data = await createCredential({
      ...(apiCompanyId ? { companyId: apiCompanyId } : {}),
      effectiveFrom: toIsoDate(values.effectiveFrom),
      effectiveTo: values.effectiveTo ? toIsoDate(values.effectiveTo) : null,
    });

    const created = extractCreatedKey(data);
    if (created != null) {
      setCreatedApiKey(created);
    }
    return created;
  };

  const handleCreateClose = (): void => {
    setCreatedApiKey(null);
    closeCreate();
  };

  const handleEditSubmit = (values: AdvisorApiKeyFormValues): void => {
    if (!selectedCredential?.id) return;

    updateCredential(
      {
        id: selectedCredential.id,
        effectiveFrom: toIsoDate(values.effectiveFrom),
        effectiveTo: values.effectiveTo ? toIsoDate(values.effectiveTo) : null,
      },
      {
        onSuccess: () => {
          closeEdit();
          setSelectedCredential(undefined);
        },
      },
    );
  };

  const handleRevokeConfirm = (): void => {
    if (!selectedCredential?.id) return;

    revokeCredential(
      {
        id: selectedCredential.id,
        status: ADVISOR_API_CREDENTIAL_STATUS.Revoked,
      },
      {
        onSuccess: () => {
          closeRevoke();
          setSelectedCredential(undefined);
        },
      },
    );
  };

  const openEditModal = (credential: Wealthlane_Dtos_AdvisorApi_AdvisorApiCredentialDto): void => {
    setSelectedCredential(credential);
    openEdit();
  };

  const openRevokeModal = (credential: Wealthlane_Dtos_AdvisorApi_AdvisorApiCredentialDto): void => {
    setSelectedCredential(credential);
    openRevoke();
  };

  if (isVendorContext && !qfaiCompanyId) {
    return (
      <WrapperContainer id='advisorApiConfigPageContainer'>
        {isVendorContext && (
          <GoBackBlock
            id='advisorApiConfigGoBack'
            onClick={() => {
              window.history.back();
            }}>
            <span className='material-symbols-outlined'>arrow_back</span>
            <Heading className='goBack' variant='body-s' title='Go back' />
          </GoBackBlock>
        )}
        <PageHeader id='advisorApiConfigPageHeader'>
          <Heading variant='h3' title='Advisor API Configuration' id='advisorApiConfigPageTitle' />
          <Heading
            variant='body-s'
            title={
              'Open this page from a customer record — no advisor company was selected.'
            }
            id='advisorApiConfigMissingCompanyMessage'
          />
        </PageHeader>
      </WrapperContainer>
    );
  }

  return (
    <WrapperContainer id='advisorApiConfigPageContainer'>
      {isVendorContext && (
        <GoBackBlock
          id='advisorApiConfigGoBack'
          onClick={() => {
            window.history.back();
          }}>
          <span className='material-symbols-outlined'>arrow_back</span>
          <Heading className='goBack' variant='body-s' title='Go back' />
        </GoBackBlock>
      )}

      <PageHeader id='advisorApiConfigPageHeader'>
        <div className='flex flex-wrap items-start justify-between gap-4 w-full'>
          <div>
            <Heading variant='h3' title='Advisor API Configuration' id='advisorApiConfigPageTitle' />
            <Heading
              variant='body-s'
              title='Manage your API keys and enrollment settings for participant access.'
              id='advisorApiConfigPageDescription'
            />
          </div>
          <div className='flex flex-wrap items-center gap-2'>
            <Button
              id='advisorEnrollmentSettingsButton'
              title='Enrollment settings'
              variant='primary-outline'
              icon='link'
              hasId='advisor-api-config'
              as='Edit'
              onClick={openEnrollment}
            />
            <Button
              id='generateAdvisorApiKeyButton'
              title='Generate API Key'
              variant='primary'
              icon='add_circle_outline'
              hasId='advisor-api-config'
              as='Create'
              onClick={() => {
                setCreatedApiKey(null);
                openCreate();
              }}
            />
          </div>
        </div>
      </PageHeader>

      <div className='grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6'>
        <div>
          <Heading variant='body-xs' title='Company Name' className='detailLabel' />
          <Heading
            variant='body'
            title={isCompanyLoading ? '...' : (companyName || '-')}
          />
        </div>
        <div>
          <Heading variant='body-xs' title='Account ID' className='detailLabel' />
          <Heading
            variant='body'
            title={isCompanyLoading ? '...' : (companyDetails?.accountId ?? '-')}
          />
        </div>
        {isVendorContext && (
          <div>
            <Heading variant='body-xs' title='Ticker' className='detailLabel' />
            <Heading
              variant='body'
              title={isCompanyLoading ? '...' : (companyDetails?.ticker ?? '-')}
            />
          </div>
        )}
      </div>

      <div
        className='mb-6 rounded-lg px-4 py-3'
        style={{ backgroundColor: COLORS.CulturedWhite }}>
        <Heading
          variant='body-s'
          title={
            companyName
              ? `API keys let ${companyName} access participant holdings programmatically. Enrollment settings control how participants reach Wealthlane from your application (enrollment key, display name, and allowed callback hosts). Configure enrollment before sharing links with participants.`
              : 'API keys let you access participant holdings programmatically. Enrollment settings control how participants reach Wealthlane from your application. Configure enrollment before sharing links with participants.'
          }
        />
      </div>

      {isLoading ? (
        <Spinners containerClassName='py-12' />
      ) : items.length === 0 ? (
        <div className='rounded-lg border border-dashed px-6 py-12 text-center' style={{ borderColor: COLORS.CulturedWhite }}>
          <Heading variant='body-s' title='No API keys yet' />
          <Heading
            variant='body-xs'
            title='Generate a key to enable advisor integrations.'
            color={COLORS.SpanishGray}
            className='mt-2'
          />
        </div>
      ) : (
        <div className='overflow-x-auto rounded-lg border' style={{ borderColor: COLORS.CulturedWhite }}>
          <table className='min-w-full text-left'>
            <thead style={{ backgroundColor: COLORS.CulturedWhite }}>
              <tr>
                <th className='px-4 py-3'>
                  <Heading variant='body-xs' title='Key Prefix' />
                </th>
                <th className='px-4 py-3'>
                  <Heading variant='body-xs' title='Status' />
                </th>
                <th className='px-4 py-3'>
                  <Heading variant='body-xs' title='Effective From' />
                </th>
                <th className='px-4 py-3'>
                  <Heading variant='body-xs' title='Effective To' />
                </th>
                <th className='px-4 py-3 text-right'>
                  <Heading variant='body-xs' title='Actions' />
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const active = isActiveCredential(item);
                const statusLabel = active ? 'Active' : 'Revoked';

                return (
                  <tr key={item.id} className='border-t' style={{ borderColor: COLORS.CulturedWhite }}>
                    <td className='px-4 py-3'>
                      <Heading variant='body-s' title={item.apiKeyPrefix ?? '-'} />
                    </td>
                    <td className='px-4 py-3'>
                      <Tag variant={active ? 'success' : 'error'} title={statusLabel} />
                    </td>
                    <td className='px-4 py-3'>
                      <Heading variant='body-s' title={formatDate(item.effectiveFrom)} />
                    </td>
                    <td className='px-4 py-3'>
                      <Heading variant='body-s' title={formatEndDate(item.effectiveTo)} />
                    </td>
                    <td className='px-4 py-3'>
                      <div className='flex justify-end gap-2'>
                        {active && (
                          <>
                            <Button
                              title='Edit Dates'
                              variant='primary-outline'
                              hasId='advisor-api-config'
                              as='Edit'
                              onClick={() => {
                                openEditModal(item);
                              }}
                            />
                            <Button
                              title='Revoke'
                              variant='danger'
                              hasId='advisor-api-config'
                              as='Edit'
                              onClick={() => {
                                openRevokeModal(item);
                              }}
                            />
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeKeyCount > 0 && (
        <Heading
          variant='body-xs'
          title={`${activeKeyCount} active ${activeKeyCount === 1 ? 'key' : 'keys'} for ${companyName || 'this company'}.`}
          color={COLORS.SpanishGray}
          className='mt-4'
        />
      )}

      {isEnrollmentOpen && (
        <AdvisorEnrollmentConfigModal
          isOpen={isEnrollmentOpen}
          isLoading={isEnrollmentConfigLoading}
          isSaving={isSavingEnrollmentConfig}
          companyName={companyName}
          enrollmentKey={enrollmentConfig?.enrollmentKey}
          initialValues={enrollmentFormInitialValues}
          onClose={closeEnrollment}
          onSubmit={handleEnrollmentSubmit}
        />
      )}

      {isCreateOpen && (
        <CreateAdvisorApiKeyModal
          isOpen={isCreateOpen}
          isLoading={isCreating}
          createdApiKey={createdApiKey}
          onClose={handleCreateClose}
          onSubmit={handleCreateSubmit}
        />
      )}

      {isEditOpen && selectedCredential && (
        <EditAdvisorApiKeyModal
          isOpen={isEditOpen}
          isLoading={isUpdating}
          credential={selectedCredential}
          onClose={() => {
            closeEdit();
            setSelectedCredential(undefined);
          }}
          onSubmit={handleEditSubmit}
        />
      )}

      <ConfirmationModal
        isShow={isRevokeOpen}
        title={`Revoke API key ${selectedCredential?.apiKeyPrefix ?? ''}? Integrations using this key will stop working immediately.`}
        handleClose={() => {
          closeRevoke();
          setSelectedCredential(undefined);
        }}
        handleSubmit={handleRevokeConfirm}
        loading={isRevoking}
      />
    </WrapperContainer>
  );
};

export default AdvisorApiConfigPage;
